import { FeatureCollection } from 'geojson'
import { convertKMZToKML } from './convertKMZToKML'
import { kml as convertKML } from '@mapbox/togeojson'

/**
 * Converts a KMZ or KML file to a GeoJSON FeatureCollection.
 * @param {File} file - The KMZ or KML file to be converted.
 * @returns {Promise<FeatureCollection>} - A promise that resolves to the GeoJSON FeatureCollection.
 * @throws {Error} - Throws an error if the input is not a valid KMZ/KML file or conversion fails.
 */
export const convertKMLToGeoJSON = async (
  file: File,
): Promise<FeatureCollection> => {
  if (!file) {
    throw new Error('No KMZ/KML file provided.')
  }

  if (!file.name.endsWith('.kmz') && !file.name.endsWith('.kml')) {
    throw new Error(
      'Unsupported file type. Only KMZ and KML files are supported.',
    )
  }

  // Get KML content (either from KMZ conversion or directly from KML file)
  const getKmlContent = async (): Promise<string> => {
    if (file.name.endsWith('.kmz')) {
      return convertKMZToKML(file)
    }

    return file.text() // Assumes it's a .kml file
  }

  const kmlContent = await getKmlContent()

  // Parse the KML content into a DOM Document
  const parser = new DOMParser()
  const kmlDom = parser.parseFromString(kmlContent, 'text/xml')

  // Convert the KML DOM to GeoJSON using the convertKML function
  return convertKML(kmlDom)
}
